/**
 * Pure shaping function: converts the existing BE calculate response + brand
 * metadata into the `MarketerViewData` shape consumed by the page components.
 *
 * Deterministic parts pull straight from the BE response. AI-generated parts
 * delegate to the Phase-1 placeholder in `config/placeholderNarrative.ts` —
 * the swap-out to `MarketerViewAIService` in Phase 2 lives entirely there.
 */

import type { IBrand, IBrandEquityScoreResponse } from '../../services/nestjs-base-api/types';
import { getDisplayName } from '../../utils/elementDisplayNames';
import { bandFromClassification, bandFromScore } from './config/bandColours';
import { BOOSTERS } from './config/boosters';
import { ENGINES } from './config/engines';
import {
  buildBoostersFootnote,
  buildBoosterRead,
  buildBrokenFraming,
  buildFixFirstPlays,
  buildHeroVerdict,
  buildOpportunities,
  buildQ5,
  buildSignalNote,
  buildThemeVerdict,
  buildWorkingFraming,
} from './config/placeholderNarrative';
import { groupForSignal, SIGNAL_GROUPS } from './config/signalGroups';
import { THEMES } from './config/themes';
import type {
  BoosterScore,
  FeederEntry,
  MarketerViewData,
  ScoreGroup,
  ScoreNode,
  StatementDetail,
  ThemeBucket,
} from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function distributionFromMap(
  dist: { [score: string]: number } | null | undefined,
): readonly number[] {
  if (!dist) return [];
  // Bipolar likert is stored 1..7; bucket length is the max key + 1 to be
  // safe with NPS (0..10) too. Caller decides what the indices mean.
  const keys = Object.keys(dist).map((k) => Number(k));
  if (keys.length === 0) return [];
  const max = Math.max(...keys);
  const out = new Array(max + 1).fill(0);
  for (const [k, v] of Object.entries(dist)) {
    const idx = Number(k);
    if (Number.isFinite(idx) && idx >= 0) out[idx] = v;
  }
  return out;
}

function meanScoreFromStatements(
  statements: readonly StatementDetail[],
): number | null {
  if (statements.length === 0) return null;
  const valid = statements.filter((s) => Number.isFinite(s.score));
  if (valid.length === 0) return null;
  const totalN = valid.reduce((acc, s) => acc + s.responseCount, 0);
  if (totalN === 0) {
    return valid.reduce((acc, s) => acc + s.score, 0) / valid.length;
  }
  return valid.reduce((acc, s) => acc + s.score * s.responseCount, 0) / totalN;
}

function average(values: readonly (number | null | undefined)[]): number {
  const valid = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  if (valid.length === 0) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}


// ---------------------------------------------------------------------------
// BE response → ScoreNode index
// ---------------------------------------------------------------------------

interface ScoreNodeBuildContext {
  /** Looked up by canonical name. */
  byCanonical: Map<string, ScoreNode>;
}

function buildScoreNodesIndex(
  response: IBrandEquityScoreResponse,
): ScoreNodeBuildContext {
  const byCanonical = new Map<string, ScoreNode>();

  // Question categories → signal-level nodes.
  for (const qc of response.questionCategoryScores) {
    const statements: StatementDetail[] = (qc.input ?? []).map((stmt) => ({
      text: stmt.text,
      // Bipolar -100..+100 polarity score — same field the v3 viz shows, and the
      // scale StatementCard's formatBipolarScore/bandFromScore expect. (Was using
      // brandPowerPercentage, a 0-100 figure, which mismatched the viz.)
      score: stmt.meanScore ?? 0,
      responseCount: stmt.responseCount,
      distribution: distributionFromMap(stmt.scoreDistribution),
    }));
    const value = qc.meanScore;
    const classification = qc.classificationLabel ?? null;
    const node: ScoreNode = {
      canonical: qc.category,
      display: getDisplayName(qc.category),
      value,
      classification,
      band: classification != null ? bandFromClassification(classification) : bandFromScore(value),
      meaning: '', // filled per-context where needed
      note: '',
      aiSummary: qc.aiSummary,
      group: groupForSignal(qc.category),
      statements: statements.length > 0 ? statements : null,
      locked: qc.locked,
    };
    node.note = buildSignalNote(node);
    byCanonical.set(qc.category, node);
  }

  // Latent elements → node entries too (engines + drivers + composite roll-ups).
  // The booster scores below pull from these.
  for (const le of response.latentElementScores) {
    if (byCanonical.has(le.element)) continue; // QC shadow wins (Associations/Touchpoints exist as both)
    const value = le.score;
    const classification = le.classificationLabel ?? null;
    const node: ScoreNode = {
      canonical: le.element,
      display: getDisplayName(le.element),
      value,
      classification,
      band: classification != null ? bandFromClassification(classification) : bandFromScore(value),
      meaning: '',
      note: '',
      aiSummary: le.aiSummary,
      group: groupForSignal(le.element),
      statements: null,
      locked: le.locked,
    };
    node.note = buildSignalNote(node);
    byCanonical.set(le.element, node);
  }

  return { byCanonical };
}

// ---------------------------------------------------------------------------
// Boosters / Engines / Themes / Signals derivation
// ---------------------------------------------------------------------------

function buildBoosters(
  ctx: ScoreNodeBuildContext,
  response: IBrandEquityScoreResponse,
): BoosterScore[] {
  return BOOSTERS.map((cfg) => {
    const le = response.latentElementScores.find(
      (l) => l.element === cfg.canonical,
    );
    const fallback = ctx.byCanonical.get(cfg.canonical);
    const value = le?.score ?? fallback?.value ?? null;
    const classification = le?.classificationLabel ?? fallback?.classification ?? null;
    const node: BoosterScore = {
      canonical: cfg.canonical,
      display: getDisplayName(cfg.canonical),
      value,
      classification,
      band: classification != null ? bandFromClassification(classification) : bandFromScore(value),
      meaning: cfg.meaning,
      note: '', // set below using the band-derived placeholder
      group: 'love', // boosters aren't grouped love/know — assign love as a safe default
      statements: null,
      locked: le?.locked ?? fallback?.locked,
      key: cfg.key,
      axis: cfg.axis,
      pathLabel: cfg.pathLabel,
      indirect: cfg.indirect,
      pathStrength: cfg.pathStrength,
    };
    node.note = buildBoosterRead(node);
    return node;
  });
}

function buildEngineNodes(
  ctx: ScoreNodeBuildContext,
  response: IBrandEquityScoreResponse,
): ScoreNode[] {
  return ENGINES.map((cfg) => {
    const le = response.latentElementScores.find(
      (l) => l.element === cfg.canonical,
    );
    const fallback = ctx.byCanonical.get(cfg.canonical);
    const value = le?.score ?? fallback?.value ?? null;
    const classification = le?.classificationLabel ?? fallback?.classification ?? null;
    const node: ScoreNode = {
      canonical: cfg.canonical,
      display: getDisplayName(cfg.canonical),
      value,
      classification,
      band: classification != null ? bandFromClassification(classification) : bandFromScore(value),
      meaning: cfg.meaning,
      note: '',
      group: 'value' as ScoreGroup, // engines aren't grouped — defaults are visually neutral
      statements: null,
    };
    node.note = buildSignalNote(node);
    return node;
  });
}

/**
 * Build a feeder map from `latentElementScores[].inputs[]` — the API already
 * exposes per-LE inputs, so the orbital model uses them as the source of
 * truth rather than maintaining a duplicate topology config.
 *
 * Marks Price Deals feeders with `sign: -1` to render the dashed-down line
 * in the orbital — mechanically Price Deals has a positive weight on its
 * parent LE but on an inverse-scaled signal, so the marketer-facing read is
 * "discount activity drags this LE down".
 */
function buildFeedersByElement(
  ctx: ScoreNodeBuildContext,
  response: IBrandEquityScoreResponse,
): Record<string, FeederEntry[]> {
  const out: Record<string, FeederEntry[]> = {};
  for (const le of response.latentElementScores) {
    const entries: FeederEntry[] = [];
    for (const input of le.inputs ?? []) {
      const node = ctx.byCanonical.get(input.name);
      const value = input.value ?? node?.value ?? null;
      const classification = node?.classification ?? null;
      const band =
        classification != null ? bandFromClassification(classification) : bandFromScore(value);
      entries.push({
        canonical: input.name,
        display: getDisplayName(input.name),
        type: input.type,
        value,
        band,
        sign: input.name === 'Price Deals' ? -1 : 1,
      });
    }
    out[le.element] = entries;
  }
  return out;
}

function buildThemes(ctx: ScoreNodeBuildContext): ThemeBucket[] {
  return THEMES.map((cfg) => {
    const signals = cfg.signals
      .map((c) => ctx.byCanonical.get(c))
      .filter((s): s is ScoreNode => s != null)
      .slice()
      .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
    return {
      id: cfg.id,
      title: cfg.title,
      covers: cfg.covers,
      verdict: buildThemeVerdict(signals),
      tone: cfg.tone,
      icon: cfg.icon,
      signals,
    };
  });
}

function pickBriefingSignals(
  ctx: ScoreNodeBuildContext,
  group: ScoreGroup,
): ScoreNode[] {
  return SIGNAL_GROUPS[group]
    .map((c) => ctx.byCanonical.get(c))
    .filter((s): s is ScoreNode => s != null);
}

// ---------------------------------------------------------------------------
// Period label
// ---------------------------------------------------------------------------

const PERIOD_LABEL: Record<string, string> = {
  '30_days': 'Last 30 days',
  '60_days': 'Last 60 days',
  '90_days': 'Last 90 days',
  '120_days': 'Last 120 days',
  '180_days': 'Last 180 days',
  '365_days': 'Last 365 days',
  all_time: 'All time',
  date_range: 'Custom range',
};

// ---------------------------------------------------------------------------
// Best / worst element ranking (scale-aware)
// ---------------------------------------------------------------------------

/**
 * Rank elements by the API's classification grade — which is computed on each
 * element's OWN scale (not all elements are -100..+100), so it's the honest
 * scale-aware read of "how good". Raw score is only a within-grade tiebreak.
 */
const CLASSIFICATION_RANK: Readonly<Record<string, number>> = Object.freeze({
  'Unbelievably Strong': 6,
  Excellent: 5,
  Good: 4,
  Neutral: 3,
  'Needs Attention': 2,
  'Priority Concern': 1,
});

function goodnessRank(node: ScoreNode): number {
  if (node.classification && CLASSIFICATION_RANK[node.classification] != null) {
    return CLASSIFICATION_RANK[node.classification];
  }
  // No classification label — approximate from the 3-bucket band.
  return node.band === 'strong' ? 5 : node.band === 'watch' ? 3 : node.band === 'weak' ? 2 : 0;
}

/** Best 7 and (deduped) worst 7 measured elements, by scale-aware grade. */
function rankBestWorst(signals: ScoreNode[]): {
  best: ScoreNode[];
  worst: ScoreNode[];
} {
  const ranked = signals
    .filter((s) => s.value != null)
    .sort(
      (a, b) =>
        goodnessRank(b) - goodnessRank(a) || (b.value ?? 0) - (a.value ?? 0),
    );
  const best = ranked.slice(0, 7);
  const bestSet = new Set(best.map((s) => s.canonical));
  const worst = [...ranked]
    .reverse()
    .filter((s) => !bestSet.has(s.canonical))
    .slice(0, 7);
  return { best, worst };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function buildMarketerViewData(args: {
  brand: Pick<IBrand, 'name'>;
  response: IBrandEquityScoreResponse;
  /** Optional industry benchmark for the Q1 "vs Benchmark" badge. */
  benchmarkComparison?: { industryMean: number; sampleSize?: number } | null;
  /**
   * Active-scope period label ("Last 90 days"). Preferred over the response's
   * echoed filter so the BE card chip always matches the global scope pill.
   */
  periodLabel?: string;
  /** Point delta vs the previous equivalent period (current − previous). */
  vsPreviousDiff?: number | null;
}): MarketerViewData {
  const {
    brand,
    response,
    benchmarkComparison = null,
    periodLabel,
    vsPreviousDiff = null,
  } = args;
  const ctx = buildScoreNodesIndex(response);

  const boosters = buildBoosters(ctx, response);
  const engines = buildEngineNodes(ctx, response);
  const themes = buildThemes(ctx);

  // Q2/Q3 framing copy is now driven by actual score band — what's strong is
  // "working", what's weak is "broken" — irrespective of love/know grouping.
  // The love/know aggregates still drive the Hero contrast meters (Adir's
  // "strength among knowers vs visibility in market" frame).
  const loveSignals = pickBriefingSignals(ctx, 'love');
  const knowSignals = pickBriefingSignals(ctx, 'know');
  const allSignals = Array.from(ctx.byCanonical.values());
  const strongSignals = allSignals.filter((s) => s.band === 'strong');
  const weakSignals = allSignals.filter((s) => s.band === 'weak');

  // Hero meters: deterministic aggregates of love-group + know-group signals,
  // mapped onto a 0..100 scale for the contrast bars.
  // Bipolar -100..+100 averages — same scale as every other score in the page.
  const strengthAvg = average(loveSignals.map((s) => s.value));
  const visibilityAvg = average(knowSignals.map((s) => s.value));

  const heroCopy = buildHeroVerdict({
    brandName: brand.name,
    strengthAvg,
    visibilityAvg,
  });

  // Fix-first plays: derived from the weakest scoring nodes across the whole
  // marketer-view universe (signals + boosters not currently strong).
  const universe: ScoreNode[] = [
    ...Array.from(ctx.byCanonical.values()),
    ...boosters,
  ];
  const weakest = universe
    .filter((s) => s.value != null && s.band === 'weak')
    .sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
  const fixFirstPlays = buildFixFirstPlays(weakest);

  const signals = Array.from(ctx.byCanonical.values());
  const { best: bestElements, worst: worstElements } = rankBestWorst(signals);
  const opportunities = buildOpportunities({ fixFirstPlays, signals });
  const q5 = buildQ5({ brandName: brand.name, signals });

  // Minor structural input — Parity Preference (direct QC at weight 0.10).
  const parityPreference =
    ctx.byCanonical.get('Parity Preference') ?? {
      canonical: 'Parity Preference',
      display: 'Parity Preference',
      value: null,
      classification: null,
      band: 'na' as const,
      meaning:
        'Preference for the brand when options look otherwise equal: a direct minor input to Brand Equity.',
      note: buildSignalNote({ band: 'na' }),
      group: 'know' as ScoreGroup,
      statements: null,
    };

  const period = response.filters?.period ?? '180_days';

  const equityBand = response.overallClassificationLabel
    ? bandFromClassification(response.overallClassificationLabel)
    : bandFromScore(response.overallBrandEquityScore);

  const equityNode: ScoreNode = {
    canonical: 'Brand Equity',
    display: 'Brand Equity',
    value: response.overallBrandEquityScore,
    classification: response.overallClassificationLabel ?? null,
    band: equityBand,
    meaning:
      'Your overall Brand Equity: a −100 to +100 roll-up of how customers feel ' +
      'about you and what they buy. It is built from the seven Brand Boosters below.',
    note: buildBoostersFootnote(boosters),
    group: 'love',
    statements: null,
  };

  return {
    brand: {
      name: brand.name,
      period: periodLabel ?? PERIOD_LABEL[period] ?? period,
      vsPrevious: vsPreviousDiff,
      equity: response.overallBrandEquityScore,
      equityClassification: response.overallClassificationLabel ?? '—',
      equityBand,
      totalResponses: response.totalResponses,
      calculatedAt: response.calculatedAt,
      benchmarkComparison: benchmarkComparison
        ? {
            industryMean: benchmarkComparison.industryMean,
            sampleSize: benchmarkComparison.sampleSize,
            diff: response.overallBrandEquityScore - benchmarkComparison.industryMean,
          }
        : null,
    },
    hero: {
      headline: heroCopy.headline,
      body: heroCopy.body,
      strength: {
        value: strengthAvg,
        labelId: 'marketerView.hero.strengthLabel',
        caption: heroCopy.strengthCaption,
      },
      visibility: {
        value: visibilityAvg,
        labelId: 'marketerView.hero.visibilityLabel',
        caption: heroCopy.visibilityCaption,
      },
    },
    boostersFootnote: buildBoostersFootnote(boosters),
    boosters,
    equityNode,
    workingFraming: buildWorkingFraming(strongSignals),
    brokenFraming: buildBrokenFraming(weakSignals),
    signals,
    bestElements,
    worstElements,
    fixFirstPlays,
    q5,
    opportunities,
    themes,
    model: {
      drivers: boosters,
      engines,
      minor: parityPreference,
      feedersByElement: buildFeedersByElement(ctx, response),
    },
  };
}
